import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./mapdle.css";

const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";
const COOKIE_NAME = "mapdle_state_v1";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;
const TOLERANCE_MILES = 100;
const VERY_CLOSE_GUESS_MILES = 250;
const CLOSE_GUESS_MILES = 500;
const EARTH_RADIUS_MILES = 3958.7613;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAILY_EPOCH_YEAR = 2026;
const DAILY_SCHEDULE_START_SEED = 145;
const WORLD_CENTER = { lat: 8, lng: 0 };
const WORLD_BOUNDS = [
  [-180, -58],
  [180, 82],
];
const WORLD_MAX_ZOOM_OFFSET = 3;
const WORLD_ZOOM = 0.6;
const SHARE_DISTANCE_BUCKETS = [
  { maxMiles: TOLERANCE_MILES, marker: "🟩" },
  { maxMiles: TOLERANCE_MILES * 2.5, marker: "🟨" },
  { maxMiles: TOLERANCE_MILES * 5, marker: "🟧" },
  { maxMiles: TOLERANCE_MILES * 10, marker: "🟥" },
  { maxMiles: Infinity, marker: "⬛" },
];

const CLUE_STEPS = [
  { id: "street", label: "Street", zoom: 13.7 },
  { id: "district", label: "District", zoom: 11.8 },
  { id: "metro", label: "Metro", zoom: 9.8 },
  { id: "region", label: "Region", zoom: 7.7 },
  { id: "country", label: "Country", zoom: 5.6 },
  { id: "continent", label: "Continent", zoom: 3.65 },
];

const LOCATIONS = [
  {
    id: "gibraltar",
    name: "Rock of Gibraltar",
    coordinates: { lat: 36.14077, lng: -5.35359 },
  },
  {
    id: "panama-canal",
    name: "Gatun Locks, Panama Canal",
    coordinates: { lat: 9.2727, lng: -79.9221 },
  },
  {
    id: "colosseum",
    name: "Colosseum",
    coordinates: { lat: 41.89021, lng: 12.49223 },
  },
  {
    id: "victoria-falls",
    name: "Victoria Falls",
    coordinates: { lat: -17.92432, lng: 25.85692 },
  },
  {
    id: "bosphorus",
    name: "Bosphorus Bridge",
    coordinates: { lat: 41.04583, lng: 29.03444 },
  },
  {
    id: "mount-fuji",
    name: "Mount Fuji",
    coordinates: { lat: 35.36063, lng: 138.72736 },
  },
  {
    id: "sydney-harbour",
    name: "Sydney Harbour Bridge",
    coordinates: { lat: -33.85231, lng: 151.21079 },
  },
  {
    id: "eiffel-tower",
    name: "Eiffel Tower",
    coordinates: { lat: 48.85837, lng: 2.29448 },
  },
  {
    id: "statue-of-liberty",
    name: "Statue of Liberty",
    coordinates: { lat: 40.68925, lng: -74.0445 },
  },
  {
    id: "taj-mahal",
    name: "Taj Mahal",
    coordinates: { lat: 27.17514, lng: 78.04214 },
  },
  {
    id: "great-pyramid",
    name: "Great Pyramid of Giza",
    coordinates: { lat: 29.97917, lng: 31.1342 },
  },
  {
    id: "golden-gate",
    name: "Golden Gate Bridge",
    coordinates: { lat: 37.81993, lng: -122.47826 },
  },
  {
    id: "christ-redeemer",
    name: "Christ the Redeemer",
    coordinates: { lat: -22.95192, lng: -43.21049 },
  },
  {
    id: "niagara-falls",
    name: "Niagara Falls",
    coordinates: { lat: 43.08153, lng: -79.07424 },
  },
  {
    id: "burj-khalifa",
    name: "Burj Khalifa",
    coordinates: { lat: 25.1972, lng: 55.2744 },
  },
  {
    id: "angkor-wat",
    name: "Angkor Wat",
    coordinates: { lat: 13.41247, lng: 103.86699 },
  },
  {
    id: "petra",
    name: "Petra",
    coordinates: { lat: 30.32846, lng: 35.44436 },
  },
  {
    id: "stonehenge",
    name: "Stonehenge",
    coordinates: { lat: 51.17888, lng: -1.82622 },
  },
  {
    id: "mount-rushmore",
    name: "Mount Rushmore",
    coordinates: { lat: 43.8791, lng: -103.45907 },
  },
  {
    id: "forbidden-city",
    name: "Forbidden City",
    coordinates: { lat: 39.91634, lng: 116.39715 },
  },
  {
    id: "mount-everest",
    name: "Mount Everest",
    coordinates: { lat: 27.9881, lng: 86.925 },
  },
  {
    id: "table-mountain",
    name: "Table Mountain",
    coordinates: { lat: -33.96282, lng: 18.40984 },
  },
  {
    id: "matterhorn",
    name: "Matterhorn",
    coordinates: { lat: 45.9763, lng: 7.6586 },
  },
  {
    id: "tower-bridge",
    name: "Tower Bridge",
    coordinates: { lat: 51.50546, lng: -0.07536 },
  },
  {
    id: "hoover-dam",
    name: "Hoover Dam",
    coordinates: { lat: 36.01562, lng: -114.73776 },
  },
  {
    id: "chichen-itza",
    name: "Chichen Itza",
    coordinates: { lat: 20.68428, lng: -88.56778 },
  },
  {
    id: "neuschwanstein",
    name: "Neuschwanstein Castle",
    coordinates: { lat: 47.55757, lng: 10.7498 },
  },
  {
    id: "palm-jumeirah",
    name: "Palm Jumeirah",
    coordinates: { lat: 25.1124, lng: 55.139 },
  },
  {
    id: "sagrada-familia",
    name: "Sagrada Familia",
    coordinates: { lat: 41.40363, lng: 2.17436 },
  },
  {
    id: "marina-bay-sands",
    name: "Marina Bay Sands",
    coordinates: { lat: 1.2834, lng: 103.8607 },
  },
  {
    id: "tokyo-tower",
    name: "Tokyo Tower",
    coordinates: { lat: 35.65858, lng: 139.74543 },
  },
  {
    id: "hagia-sophia",
    name: "Hagia Sophia",
    coordinates: { lat: 41.00858, lng: 28.98018 },
  },
  {
    id: "leaning-tower-pisa",
    name: "Leaning Tower of Pisa",
    coordinates: { lat: 43.72295, lng: 10.3966 },
  },
  {
    id: "hollywood-sign",
    name: "Hollywood Sign",
    coordinates: { lat: 34.1341, lng: -118.3215 },
  },
  {
    id: "alcatraz",
    name: "Alcatraz Island",
    coordinates: { lat: 37.8267, lng: -122.423 },
  },
  {
    id: "acropolis",
    name: "Acropolis of Athens",
    coordinates: { lat: 37.97153, lng: 23.72666 },
  },
  {
    id: "brandenburg-gate",
    name: "Brandenburg Gate",
    coordinates: { lat: 52.51627, lng: 13.3777 },
  },
  {
    id: "cn-tower",
    name: "CN Tower",
    coordinates: { lat: 43.64257, lng: -79.38705 },
  },
  {
    id: "grand-canyon",
    name: "Grand Canyon South Rim",
    coordinates: { lat: 36.0544, lng: -112.1401 },
  },
  {
    id: "mount-vesuvius",
    name: "Mount Vesuvius",
    coordinates: { lat: 40.821, lng: 14.426 },
  },
  {
    id: "great-wall-badaling",
    name: "Great Wall of China, Badaling",
    coordinates: { lat: 40.35978, lng: 116.02037 },
  },
  {
    id: "louvre-pyramid",
    name: "Louvre Pyramid",
    coordinates: { lat: 48.86061, lng: 2.33764 },
  },
  {
    id: "notre-dame-paris",
    name: "Notre-Dame de Paris",
    coordinates: { lat: 48.85297, lng: 2.3499 },
  },
  {
    id: "buckingham-palace",
    name: "Buckingham Palace",
    coordinates: { lat: 51.50136, lng: -0.14189 },
  },
  {
    id: "big-ben",
    name: "Big Ben",
    coordinates: { lat: 51.50073, lng: -0.12463 },
  },
  {
    id: "times-square",
    name: "Times Square",
    coordinates: { lat: 40.758, lng: -73.9855 },
  },
  {
    id: "space-needle",
    name: "Space Needle",
    coordinates: { lat: 47.62051, lng: -122.34928 },
  },
  {
    id: "gateway-arch",
    name: "Gateway Arch",
    coordinates: { lat: 38.62469, lng: -90.18478 },
  },
  {
    id: "st-basils-cathedral",
    name: "St. Basil's Cathedral",
    coordinates: { lat: 55.75252, lng: 37.62309 },
  },
  {
    id: "palace-versailles",
    name: "Palace of Versailles",
    coordinates: { lat: 48.80486, lng: 2.12036 },
  },
  {
    id: "mont-saint-michel",
    name: "Mont Saint-Michel",
    coordinates: { lat: 48.63606, lng: -1.51146 },
  },
  {
    id: "iguazu-falls",
    name: "Iguazu Falls",
    coordinates: { lat: -25.6953, lng: -54.4367 },
  },
  {
    id: "mount-kilimanjaro",
    name: "Mount Kilimanjaro",
    coordinates: { lat: -3.0674, lng: 37.3556 },
  },
  {
    id: "fushimi-inari",
    name: "Fushimi Inari Shrine",
    coordinates: { lat: 34.96714, lng: 135.77267 },
  },
  {
    id: "kiyomizu-dera",
    name: "Kiyomizu-dera",
    coordinates: { lat: 34.99486, lng: 135.785 },
  },
  {
    id: "potala-palace",
    name: "Potala Palace",
    coordinates: { lat: 29.6578, lng: 91.1169 },
  },
  {
    id: "borobudur",
    name: "Borobudur",
    coordinates: { lat: -7.60787, lng: 110.20375 },
  },
  {
    id: "prambanan",
    name: "Prambanan",
    coordinates: { lat: -7.752, lng: 110.4915 },
  },
  {
    id: "ahu-tongariki",
    name: "Ahu Tongariki",
    coordinates: { lat: -27.1259, lng: -109.2768 },
  },
  {
    id: "terracotta-army",
    name: "Terracotta Army",
    coordinates: { lat: 34.3849, lng: 109.278 },
  },
  {
    id: "alhambra",
    name: "Alhambra",
    coordinates: { lat: 37.17608, lng: -3.58814 },
  },
  {
    id: "giants-causeway",
    name: "Giant's Causeway",
    coordinates: { lat: 55.2408, lng: -6.5116 },
  },
  {
    id: "cliffs-of-moher",
    name: "Cliffs of Moher",
    coordinates: { lat: 52.9715, lng: -9.4309 },
  },
  {
    id: "old-faithful",
    name: "Old Faithful",
    coordinates: { lat: 44.4605, lng: -110.8281 },
  },
  {
    id: "devils-tower",
    name: "Devils Tower",
    coordinates: { lat: 44.5902, lng: -104.7156 },
  },
  {
    id: "mount-rainier",
    name: "Mount Rainier",
    coordinates: { lat: 46.8523, lng: -121.7603 },
  },
  {
    id: "lake-louise",
    name: "Lake Louise",
    coordinates: { lat: 51.417, lng: -116.217 },
  },
  {
    id: "western-wall",
    name: "Western Wall",
    coordinates: { lat: 31.7767, lng: 35.2345 },
  },
  {
    id: "edinburgh-castle",
    name: "Edinburgh Castle",
    coordinates: { lat: 55.94861, lng: -3.19991 },
  },
  {
    id: "sacre-coeur",
    name: "Sacre-Coeur",
    coordinates: { lat: 48.8867, lng: 2.3431 },
  },
];

let stylePromise;

function getSeedForDateParts(year, monthIndex, day) {
  const epoch = Date.UTC(DAILY_EPOCH_YEAR, 0, 1);
  const date = Date.UTC(year, monthIndex, day);
  return Math.max(0, Math.floor((date - epoch) / DAY_MS));
}

function getDailySeed() {
  const now = new Date();
  return getSeedForDateParts(now.getFullYear(), now.getMonth(), now.getDate());
}

function getDailyLabel(seed) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(DAILY_EPOCH_YEAR, 0, seed + 1));
}

function formatDateInputFromSeed(seed) {
  const date = new Date(DAILY_EPOCH_YEAR, 0, seed + 1);
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getSeedFromDateInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (!match) {
    return getDailySeed();
  }

  return getSeedForDateParts(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function positiveModulo(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

function getDailyLocation(seed) {
  const scheduleIndex = positiveModulo(
    seed - DAILY_SCHEDULE_START_SEED,
    LOCATIONS.length
  );

  return LOCATIONS[scheduleIndex];
}

function getDailyScheduleIndex(seed) {
  return positiveModulo(seed - DAILY_SCHEDULE_START_SEED, LOCATIONS.length);
}

function getCookieValue(name) {
  if (typeof document === "undefined") {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split("; ") : [];
  const cookie = cookies.find((entry) => entry.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  return cookie.slice(name.length + 1);
}

function normalizeAttempt(attempt) {
  const lat = Number(attempt && attempt.guess && attempt.guess.lat);
  const lng = Number(attempt && attempt.guess && attempt.guess.lng);
  const distanceMiles = Number(attempt && attempt.distanceMiles);
  const number = Number(attempt && attempt.number);
  const stage = Number(attempt && attempt.stage);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    !Number.isFinite(distanceMiles) ||
    !Number.isFinite(number)
  ) {
    return null;
  }

  return {
    distanceMiles,
    guess: { lat, lng },
    number,
    stage: Number.isFinite(stage) ? clamp(stage, 0, CLUE_STEPS.length - 1) : 0,
  };
}

function readSavedGameState(seed) {
  const cookieValue = getCookieValue(COOKIE_NAME);

  if (!cookieValue) {
    return null;
  }

  try {
    const savedState = JSON.parse(decodeURIComponent(cookieValue));

    if (!savedState || savedState.seed !== seed) {
      return null;
    }

    const attempts = Array.isArray(savedState.attempts)
      ? savedState.attempts.map(normalizeAttempt).filter(Boolean)
      : [];
    const status = ["playing", "won", "lost"].includes(savedState.status)
      ? savedState.status
      : "playing";
    const stage = clamp(Number(savedState.stage) || 0, 0, CLUE_STEPS.length - 1);

    return {
      attempts,
      seed,
      stage,
      status,
      viewedClue: clamp(
        Number.isFinite(Number(savedState.viewedClue))
          ? Number(savedState.viewedClue)
          : stage,
        0,
        stage
      ),
    };
  } catch {
    return null;
  }
}

function createInitialGameState() {
  const seed = getDailySeed();
  return (
    readSavedGameState(seed) || {
      attempts: [],
      seed,
      stage: 0,
      status: "playing",
      viewedClue: 0,
    }
  );
}

function saveGameState({ attempts, seed, stage, status, viewedClue }) {
  if (typeof document === "undefined") {
    return;
  }

  const encodedState = encodeURIComponent(
    JSON.stringify({
      attempts,
      seed,
      stage,
      status,
      viewedClue,
    })
  );

  document.cookie = `${COOKIE_NAME}=${encodedState}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
}

function hideLabelLayers(style) {
  return {
    ...style,
    layers: style.layers.map((layer) => {
      const layerId = layer.id.toLowerCase();
      const hasTextField = Boolean(layer.layout && layer.layout["text-field"]);
      const isNamedLayer =
        layerId.includes("label") ||
        layerId.includes("name") ||
        layerId.includes("poi") ||
        layerId.includes("housenumber");

      if (layer.type === "symbol" || hasTextField || isNamedLayer) {
        return {
          ...layer,
          layout: {
            ...(layer.layout || {}),
            visibility: "none",
          },
        };
      }

      return layer;
    }),
  };
}

function hideCountryBoundaryLayers(style) {
  return {
    ...style,
    layers: style.layers.map((layer) => {
      const layerId = layer.id.toLowerCase();
      const isCountryBoundaryLayer =
        layer["source-layer"] === "boundary" &&
        (layerId === "boundary_2" || layerId === "boundary_disputed");

      if (isCountryBoundaryLayer) {
        return {
          ...layer,
          layout: {
            ...(layer.layout || {}),
            visibility: "none",
          },
        };
      }

      return layer;
    }),
  };
}

function applyProjection(style, projection) {
  if (!projection) {
    return style;
  }

  return {
    ...style,
    projection: {
      type: projection,
    },
  };
}

function cloneStyle(style) {
  return JSON.parse(JSON.stringify(style));
}

function getLabelFreeStyle() {
  if (!stylePromise) {
    stylePromise = fetch(STYLE_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Map style failed to load");
        }

        return response.json();
      })
      .then(hideLabelLayers);
  }

  return stylePromise.then(cloneStyle);
}

function toRadians(value) {
  return (value * Math.PI) / 180;
}

function getDistanceMiles(first, second) {
  const deltaLat = toRadians(second.lat - first.lat);
  const deltaLng = toRadians(second.lng - first.lng);
  const lat1 = toRadians(first.lat);
  const lat2 = toRadians(second.lat);
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatCoordinate(value) {
  return value.toFixed(5);
}

function formatCoordinates(point) {
  return `${formatCoordinate(point.lat)}, ${formatCoordinate(point.lng)}`;
}

function formatDistance(distance) {
  if (distance == null) {
    return "--";
  }

  return distance < 10 ? `${distance.toFixed(1)} mi` : `${Math.round(distance)} mi`;
}

function getShareMarker(distanceMiles) {
  return SHARE_DISTANCE_BUCKETS.find((bucket) => distanceMiles <= bucket.maxMiles).marker;
}

function getOrderedAttempts(attempts) {
  return [...attempts].reverse();
}

function buildShareText({ attempts, seed, status }) {
  const orderedAttempts = getOrderedAttempts(attempts);
  const won = status === "won";
  const finalAttempt = orderedAttempts[orderedAttempts.length - 1];
  const score = won && finalAttempt ? `${finalAttempt.number}/${CLUE_STEPS.length}` : `X/${CLUE_STEPS.length}`;
  const lines = [`Mapdle ${getDailyLabel(seed)} ${score}`];

  if (orderedAttempts.length) {
    lines.push(
      "",
      ...orderedAttempts.map(
        (attempt) =>
          `${getShareMarker(attempt.distanceMiles)} Guess ${attempt.number}: ${formatDistance(
            attempt.distanceMiles
          )}`
      )
    );
  } else {
    lines.push("", "No guesses submitted");
  }

  if (typeof window !== "undefined" && window.location && window.location.origin) {
    lines.push(`${window.location.origin}/mapdle`);
  }

  return lines.join("\n");
}

async function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back to a temporary textarea for browsers that block clipboard writes.
    }
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

function getGoogleLink(point) {
  return `https://www.google.com/maps/search/?api=1&query=${point.lat},${point.lng}`;
}

function createMarkerElement(
  type,
  label,
  { isClose = false, isVeryClose = false } = {}
) {
  const marker = document.createElement("div");
  marker.className = [
    "mapdleMarker",
    `mapdleMarker-${type}`,
    isClose ? "isClose" : "",
    isVeryClose ? "isVeryClose" : "",
  ]
    .filter(Boolean)
    .join(" ");
  marker.setAttribute("aria-hidden", "true");

  const pin = document.createElement("span");
  pin.className = "mapdleMarkerPin";
  pin.textContent = label;

  marker.append(pin);
  return marker;
}

function clearMarker(markerRef) {
  if (markerRef.current) {
    markerRef.current.remove();
    markerRef.current = null;
  }
}

function MapPanel({
  answer,
  ariaLabel,
  bounds,
  center,
  children,
  className = "",
  guess,
  guesses = [],
  hideCountryBorders = false,
  interactive = false,
  maxZoomOffset,
  onSelect,
  projection,
  revealAnswer = false,
  showScale = false,
  zoom,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const guessMarkerRef = useRef(null);
  const guessMarkersRef = useRef([]);
  const answerMarkerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [mapState, setMapState] = useState("loading");

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let isDisposed = false;
    const maplibregl = window.maplibregl;

    if (!containerRef.current || !maplibregl) {
      setMapState("error");
      return undefined;
    }

    setMapState("loading");

    getLabelFreeStyle()
      .then((style) =>
        hideCountryBorders ? hideCountryBoundaryLayers(style) : style
      )
      .then((style) => applyProjection(style, projection))
      .then((style) => {
        if (isDisposed || !containerRef.current) {
          return;
        }

        const map = new maplibregl.Map({
          attributionControl: false,
          center: [center.lng, center.lat],
          container: containerRef.current,
          dragRotate: false,
          interactive: true,
          pitchWithRotate: false,
          style,
          zoom,
        });

        mapRef.current = map;

        map.dragRotate.disable();
        map.touchZoomRotate.disableRotation();

        if (interactive) {
          map.addControl(
            new maplibregl.NavigationControl({
              showCompass: false,
            }),
            "top-right"
          );
        } else {
          map.scrollZoom.disable();
          map.boxZoom.disable();
          map.dragPan.disable();
          map.keyboard.disable();
          map.doubleClickZoom.disable();
          map.touchZoomRotate.disable();
        }

        if (showScale) {
          map.addControl(
            new maplibregl.ScaleControl({
              maxWidth: 112,
              unit: "imperial",
            }),
            "bottom-left"
          );
        }

        map.getCanvas().style.cursor = interactive ? "crosshair" : "default";

        map.on("click", (event) => {
          if (!interactive || !onSelectRef.current) {
            return;
          }

          onSelectRef.current({
            lat: event.lngLat.lat,
            lng: event.lngLat.lng,
          });
        });

        map.on("load", () => {
          if (isDisposed) {
            return;
          }

          if (bounds) {
            map.fitBounds(bounds, {
              duration: 0,
              padding: 10,
            });

            if (interactive && maxZoomOffset) {
              const fittedZoom = map.getZoom();
              map.setMinZoom(fittedZoom);
              map.setMaxZoom(fittedZoom + maxZoomOffset);
            }
          }

          setMapState("ready");
          map.resize();
        });
      })
      .catch((error) => {
        console.error(error);
        if (!isDisposed) {
          setMapState("error");
        }
      });

    return () => {
      isDisposed = true;
      clearMarker(guessMarkerRef);
      clearMarker(answerMarkerRef);
      guessMarkersRef.current.forEach((marker) => marker.remove());
      guessMarkersRef.current = [];

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [
    bounds,
    center.lat,
    center.lng,
    hideCountryBorders,
    interactive,
    maxZoomOffset,
    projection,
    showScale,
    zoom,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    const maplibregl = window.maplibregl;

    if (mapState !== "ready" || !map || !maplibregl) {
      return undefined;
    }

    clearMarker(guessMarkerRef);
    clearMarker(answerMarkerRef);
    guessMarkersRef.current.forEach((marker) => marker.remove());
    guessMarkersRef.current = [];

    guessMarkersRef.current = guesses.map((previousGuess) =>
      new maplibregl.Marker({
        anchor: "center",
        element: createMarkerElement("previous", previousGuess.label, {
          isClose: previousGuess.isClose,
          isVeryClose: previousGuess.isVeryClose,
        }),
      })
        .setLngLat([previousGuess.lng, previousGuess.lat])
        .addTo(map)
    );

    if (guess) {
      guessMarkerRef.current = new maplibregl.Marker({
        anchor: "center",
        element: createMarkerElement("guess", ""),
      })
        .setLngLat([guess.lng, guess.lat])
        .addTo(map);
    }

    if (revealAnswer && answer) {
      answerMarkerRef.current = new maplibregl.Marker({
        anchor: "center",
        element: createMarkerElement("answer", "A"),
      })
        .setLngLat([answer.lng, answer.lat])
        .addTo(map);
    }

    return () => {
      clearMarker(guessMarkerRef);
      clearMarker(answerMarkerRef);
      guessMarkersRef.current.forEach((marker) => marker.remove());
      guessMarkersRef.current = [];
    };
  }, [
    answer,
    guess,
    guesses,
    mapState,
    revealAnswer,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;

    if (
      mapState !== "ready" ||
      !map ||
      !container ||
      typeof window.ResizeObserver === "undefined"
    ) {
      return undefined;
    }

    let animationFrameId = null;
    const resizeObserver = new window.ResizeObserver(() => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        map.resize();
      });
    });

    resizeObserver.observe(container);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver.disconnect();
    };
  }, [mapState]);

  return (
    <div className={`mapdleMapFrame ${className}`} aria-label={ariaLabel}>
      <div className="mapdleMapCanvas" ref={containerRef} />
      {mapState === "loading" && (
        <div className="mapdleMapNotice">Loading map</div>
      )}
      {mapState === "error" && (
        <div className="mapdleMapNotice mapdleMapNotice-error">
          Map data unavailable
        </div>
      )}
      {children}
    </div>
  );
}

function ClueReticle() {
  return (
    <div className="mapdleReticle" aria-hidden="true">
      <span />
    </div>
  );
}

function CluePreview({ index, step, target }) {
  return (
    <div className="mapdleWinClue">
      <div className="mapdleWinClueHeader">
        <span>Clue {index + 1}</span>
        <span>{step.label}</span>
      </div>
      <MapPanel
        ariaLabel={`Winning clue ${index + 1}`}
        center={target}
        className="mapdleClueFrame mapdleWinClueFrame"
        showScale
        zoom={step.zoom}
      >
        <ClueReticle />
      </MapPanel>
    </div>
  );
}

function WinningClues({ target }) {
  return (
    <section className="mapdleWinClues" aria-label="All clue images">
      <h3>All clues</h3>
      <div className="mapdleWinClueGrid">
        {CLUE_STEPS.map((step, index) => (
          <CluePreview
            index={index}
            key={step.id}
            step={step}
            target={target}
          />
        ))}
      </div>
    </section>
  );
}

function ResultPanel({
  distanceMiles,
  location,
  onCopyShare,
  status,
  target,
}) {
  const won = status === "won";

  return (
    <aside className={`mapdleResult ${won ? "isWon" : "isLost"}`}>
      <div className="mapdleResultSummary">
        <p className="mapdleResultStatus">{won ? "You won" : "Answer"}</p>
        <h2 id="mapdle-result-title">{location.name}</h2>
        {won && (
          <p className="mapdleResultCallout">
            Your guess landed within {TOLERANCE_MILES} miles.
          </p>
        )}
        <dl>
          <div>
            <dt>Coordinates</dt>
            <dd>{formatCoordinates(target)}</dd>
          </div>
          {distanceMiles != null && (
            <div>
              <dt>Distance</dt>
              <dd>{formatDistance(distanceMiles)}</dd>
            </div>
          )}
        </dl>
        <div className="mapdleResultActions">
          <a href={getGoogleLink(target)} target="_blank" rel="noreferrer">
            Open in Google
          </a>
          <button type="button" onClick={onCopyShare}>
            Share
          </button>
        </div>
        {won && <WinningClues target={target} />}
      </div>
    </aside>
  );
}

function ResultModal({ children, isOpen, onClose }) {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="mapdleModalLayer"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="mapdleModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mapdle-result-title"
      >
        <button
          className="mapdleModalClose"
          type="button"
          aria-label="Close result"
          onClick={onClose}
        >
          x
        </button>
        {children}
      </section>
    </div>
  );
}

function Toast({ message }) {
  if (!message) {
    return null;
  }

  return (
    <div className="mapdleToast" role="status" aria-live="polite">
      {message}
    </div>
  );
}

function useMobileControlOffset() {
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) {
      return undefined;
    }

    const visualViewport = window.visualViewport;

    function updateControlOffset() {
      const coveredBottom =
        window.innerHeight - visualViewport.height - visualViewport.offsetTop;
      const bottomOffset = Math.max(10, Math.ceil(coveredBottom + 10));
      document.documentElement.style.setProperty(
        "--mapdle-mobile-controls-bottom",
        `${bottomOffset}px`
      );
    }

    updateControlOffset();
    visualViewport.addEventListener("resize", updateControlOffset);
    visualViewport.addEventListener("scroll", updateControlOffset);
    window.addEventListener("resize", updateControlOffset);

    return () => {
      visualViewport.removeEventListener("resize", updateControlOffset);
      visualViewport.removeEventListener("scroll", updateControlOffset);
      window.removeEventListener("resize", updateControlOffset);
      document.documentElement.style.removeProperty(
        "--mapdle-mobile-controls-bottom"
      );
    };
  }, []);
}

export function MapdleDebugPage() {
  const [dateValue, setDateValue] = useState(() =>
    formatDateInputFromSeed(getDailySeed())
  );
  const [selectedClue, setSelectedClue] = useState(0);
  const seed = useMemo(() => getSeedFromDateInput(dateValue), [dateValue]);
  const location = useMemo(() => getDailyLocation(seed), [seed]);
  const scheduleIndex = getDailyScheduleIndex(seed);
  const target = location.coordinates;
  const selectedStep = CLUE_STEPS[selectedClue];

  function shiftDate(days) {
    setDateValue((currentValue) =>
      formatDateInputFromSeed(getSeedFromDateInput(currentValue) + days)
    );
  }

  function handleDateChange(event) {
    setDateValue(event.target.value);
  }

  return (
    <main className="mapdlePage mapdleDebugPage">
      <div className="mapdleShell">
        <header className="mapdleHeader">
          <div>
            <p className="mapdleKicker">Debug</p>
            <h1>Mapdle</h1>
          </div>
          <div className="mapdleStats" aria-label="Debug date status">
            <span>{getDailyLabel(seed)}</span>
            <span>Slot {scheduleIndex + 1}/{LOCATIONS.length}</span>
          </div>
        </header>

        <section className="mapdleDebugPanel" aria-label="Mapdle clue preview">
          <div className="mapdleDebugControls">
            <label className="mapdleDebugField">
              <span>Date</span>
              <input
                type="date"
                value={dateValue}
                onChange={handleDateChange}
                onInput={handleDateChange}
              />
            </label>
            <div className="mapdleButtonRow">
              <button
                className="mapdleSecondaryButton"
                type="button"
                onClick={() => shiftDate(-1)}
              >
                Previous day
              </button>
              <button
                className="mapdleSecondaryButton"
                type="button"
                onClick={() =>
                  setDateValue(formatDateInputFromSeed(getDailySeed()))
                }
              >
                Today
              </button>
              <button
                className="mapdleSecondaryButton"
                type="button"
                onClick={() => shiftDate(1)}
              >
                Next day
              </button>
            </div>
          </div>

          <div className="mapdleDebugCluePicker" aria-label="Clue level">
            {CLUE_STEPS.map((step, index) => (
              <button
                type="button"
                key={step.id}
                className={index === selectedClue ? "isSelected" : ""}
                aria-pressed={index === selectedClue}
                onClick={() => setSelectedClue(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <div className="mapdleDebugLayout">
            <article className="mapdleClueCard isCurrent">
              <div className="mapdleClueHeader">
                <span>Clue {selectedClue + 1}</span>
                <span>{selectedStep.label}</span>
              </div>
              <MapPanel
                ariaLabel={`${getDailyLabel(seed)} clue ${selectedClue + 1}`}
                center={target}
                className="mapdleClueFrame mapdleDebugClueFrame"
                showScale
                zoom={selectedStep.zoom}
              >
                <ClueReticle />
              </MapPanel>
            </article>

            <aside className="mapdleDebugDetails">
              <p className="mapdleResultStatus">Scheduled location</p>
              <h2>{location.name}</h2>
              <dl>
                <div>
                  <dt>Date</dt>
                  <dd>{getDailyLabel(seed)}</dd>
                </div>
                <div>
                  <dt>Coordinates</dt>
                  <dd>{formatCoordinates(target)}</dd>
                </div>
                <div>
                  <dt>Schedule slot</dt>
                  <dd>{scheduleIndex + 1} of {LOCATIONS.length}</dd>
                </div>
                <div>
                  <dt>Seed</dt>
                  <dd>{seed}</dd>
                </div>
              </dl>
            </aside>
          </div>

          <p className="mapdleAttribution">
            Map data OpenStreetMap and OpenFreeMap.
          </p>
        </section>
      </div>
    </main>
  );
}

function MapdlePage() {
  useMobileControlOffset();

  const [initialState] = useState(createInitialGameState);
  const [seed] = useState(initialState.seed);
  const [stage, setStage] = useState(initialState.stage);
  const [viewedClue, setViewedClue] = useState(initialState.viewedClue);
  const [guess, setGuess] = useState(null);
  const [attempts, setAttempts] = useState(initialState.attempts);
  const [status, setStatus] = useState(initialState.status);
  const [isResultModalOpen, setIsResultModalOpen] = useState(
    initialState.status !== "playing"
  );
  const [toastMessage, setToastMessage] = useState("");
  const location = useMemo(() => getDailyLocation(seed), [seed]);
  const target = location.coordinates;
  const latestAttempt = attempts[0];
  const latestAvailableClue = status === "won" ? CLUE_STEPS.length - 1 : stage;
  const selectedClue = Math.min(viewedClue, latestAvailableClue);
  const selectedStep = CLUE_STEPS[selectedClue];
  const availableClues = Array.from(
    { length: latestAvailableClue + 1 },
    (_, index) => index
  );
  const previousGuesses = useMemo(
    () =>
      getOrderedAttempts(attempts).map((attempt) => ({
        ...attempt.guess,
        isClose: attempt.distanceMiles <= CLOSE_GUESS_MILES,
        isVeryClose: attempt.distanceMiles <= VERY_CLOSE_GUESS_MILES,
        label: String(attempt.number),
      })),
    [attempts]
  );
  const isComplete = status !== "playing";
  const guessNumberLabel = isComplete
    ? latestAttempt
      ? `Guess number ${latestAttempt.number}`
      : "No guesses submitted"
    : `Guess number ${attempts.length + 1}`;
  const guessCountLabel = `${attempts.length} ${
    attempts.length === 1 ? "guess" : "guesses"
  }`;
  const shareText = useMemo(
    () => (isComplete ? buildShareText({ attempts, seed, status }) : ""),
    [attempts, isComplete, seed, status]
  );

  const handleGuessSelect = useCallback(
    (point) => {
      if (status === "playing") {
        setGuess(point);
      }
    },
    [status]
  );

  useEffect(() => {
    saveGameState({ attempts, seed, stage, status, viewedClue });
  }, [attempts, seed, stage, status, viewedClue]);

  useEffect(() => {
    if (isComplete) {
      setIsResultModalOpen(true);
    }
  }, [isComplete]);

  function handleSubmitGuess() {
    if (!guess || status !== "playing") {
      return;
    }

    const distanceMiles = getDistanceMiles(target, guess);
    const attempt = {
      distanceMiles,
      guess,
      number: attempts.length + 1,
      stage,
    };

    setAttempts((previousAttempts) => [attempt, ...previousAttempts]);
    setGuess(null);

    if (distanceMiles <= TOLERANCE_MILES) {
      setStatus("won");
      return;
    }

    if (stage === CLUE_STEPS.length - 1) {
      setStatus("lost");
      return;
    }

    setStage((currentStage) => {
      const nextStage = currentStage + 1;
      setViewedClue(nextStage);
      return nextStage;
    });
  }

  function handleNextClue() {
    if (status !== "playing") {
      return;
    }

    if (stage === CLUE_STEPS.length - 1) {
      setGuess(null);
      setStatus("lost");
      return;
    }

    setStage((currentStage) => {
      const nextStage = currentStage + 1;
      setViewedClue(nextStage);
      return nextStage;
    });
    setGuess(null);
  }

  async function handleCopyShare() {
    if (!shareText) {
      return;
    }

    try {
      await copyText(shareText);
      setToastMessage("Results copied");
      window.setTimeout(() => {
        setToastMessage("");
      }, 1600);
    } catch {
      setToastMessage("Copy failed");
      window.setTimeout(() => {
        setToastMessage("");
      }, 1600);
    }
  }

  function showOlderClue() {
    setViewedClue((currentClue) => Math.max(0, currentClue - 1));
  }

  function showNewerClue() {
    setViewedClue((currentClue) =>
      Math.min(latestAvailableClue, currentClue + 1)
    );
  }

  return (
    <main className="mapdlePage">
      <Toast message={toastMessage} />
      <div className="mapdleShell">
        <header className="mapdleHeader">
          <div>
            <p className="mapdleKicker">Daily {getDailyLabel(seed)}</p>
            <h1>Mapdle</h1>
          </div>
          <div className="mapdleStats" aria-label="Round status">
            <span>{TOLERANCE_MILES} mi</span>
            <span>{guessCountLabel}</span>
          </div>
        </header>

        <div className="mapdleLayout">
          <section className="mapdleClues" aria-label="Revealed clues">
            <article
              className="mapdleClueCard isCurrent"
              key={`${location.id}-${selectedStep.id}`}
            >
              <div className="mapdleClueHeader">
                <span>Clue {selectedClue + 1}</span>
                <span>{selectedStep.label}</span>
              </div>
              <MapPanel
                ariaLabel={`Clue ${selectedClue + 1}`}
                center={target}
                className="mapdleClueFrame"
                showScale
                zoom={selectedStep.zoom}
              >
                <ClueReticle />
              </MapPanel>
            </article>

            <div className="mapdleClueNav" aria-label="Clue history">
              <button
                type="button"
                title="Older clue"
                aria-label="Older clue"
                disabled={selectedClue === 0}
                onClick={showOlderClue}
              >
                &lt;
              </button>
              <div className="mapdleClueDots">
                {availableClues.map((clueIndex) => (
                  <button
                    type="button"
                    key={clueIndex}
                    className={clueIndex === selectedClue ? "isSelected" : ""}
                    aria-label={`Show clue ${clueIndex + 1}`}
                    aria-pressed={clueIndex === selectedClue}
                    onClick={() => setViewedClue(clueIndex)}
                  >
                    {clueIndex + 1}
                  </button>
                ))}
              </div>
              <button
                type="button"
                title="Newer clue"
                aria-label="Newer clue"
                disabled={selectedClue === latestAvailableClue}
                onClick={showNewerClue}
              >
                &gt;
              </button>
            </div>
          </section>

          <section className="mapdleGuessArea" aria-label="Guess">
            <MapPanel
              answer={target}
              ariaLabel="World guess map"
              bounds={WORLD_BOUNDS}
              center={WORLD_CENTER}
              className="mapdleWorldFrame"
              guess={guess}
              guesses={previousGuesses}
              hideCountryBorders
              interactive
              maxZoomOffset={WORLD_MAX_ZOOM_OFFSET}
              onSelect={handleGuessSelect}
              projection="globe"
              revealAnswer={isComplete}
              showScale
              zoom={WORLD_ZOOM}
            />

            <div className="mapdleControls">
              <div className="mapdleLastGuess">
                {guessNumberLabel}
              </div>
              <div className="mapdleButtonRow">
                {isComplete ? (
                  <button
                    className="mapdlePrimaryButton"
                    type="button"
                    onClick={() => setIsResultModalOpen(true)}
                  >
                    See result
                  </button>
                ) : (
                  <>
                    <button
                      className="mapdlePrimaryButton"
                      type="button"
                      disabled={!guess}
                      onClick={handleSubmitGuess}
                    >
                      Submit guess
                    </button>
                    <button
                      className="mapdleSecondaryButton"
                      type="button"
                      onClick={handleNextClue}
                    >
                      {stage === CLUE_STEPS.length - 1 ? "Reveal answer" : "Next clue"}
                    </button>
                  </>
                )}
              </div>
            </div>

            <p className="mapdleAttribution">
              Map data OpenStreetMap and OpenFreeMap.
            </p>
          </section>
        </div>
      </div>
      <ResultModal
        isOpen={isResultModalOpen}
        onClose={() => setIsResultModalOpen(false)}
      >
        <ResultPanel
          distanceMiles={latestAttempt && latestAttempt.distanceMiles}
          location={location}
          onCopyShare={handleCopyShare}
          status={status}
          target={target}
        />
      </ResultModal>
    </main>
  );
}

export default MapdlePage;
